import { useCallback, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, App as AntApp, Tag, theme } from 'antd';
import {
  DatabaseOutlined, DollarOutlined, TeamOutlined, LaptopOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AuthProvider, usePermissions, createAuthAxios } from '@zero/auth-client';
import { LoginForm } from '@zero/auth-admin-ui';
import { RequireAuth, SessionGate, PreAuthToolbar, setSharedAxios } from '@vmt/shared';

import { AppLayout }   from './components/AppLayout.jsx';
import { AdminLayout } from './components/AdminLayout.jsx';

import { DashboardPage }      from './pages/admin/DashboardPage.jsx';
import { UsersPage }          from './pages/admin/UsersPage.jsx';
import { RolesPage }          from './pages/admin/RolesPage.jsx';
import { PermissionsPage }    from './pages/admin/PermissionsPage.jsx';
import { PoliciesPage }       from './pages/admin/PoliciesPage.jsx';
import { AuditPage }          from './pages/admin/AuditPage.jsx';
import { ChangeLogPage }      from './pages/admin/ChangeLogPage.jsx';
import { AlertsPage }         from './pages/admin/AlertsPage.jsx';
import { ProfilePage }        from './pages/admin/ProfilePage.jsx';
import { SessionsPage }       from './pages/admin/SessionsPage.jsx';
import { BrandingPage }       from './pages/admin/BrandingPage.jsx';
import { SmtpConfigPage }     from './pages/admin/SmtpConfigPage.jsx';
import { GoogleOAuthPage }    from './pages/admin/GoogleOAuthPage.jsx';
import { SecurityConfigPage } from './pages/admin/SecurityConfigPage.jsx';

const { Title, Paragraph } = Typography;

function ModuleCard({ href, icon, title, desc, disabled = false, comingSoonLabel }) {
  const { token } = theme.useToken();
  const card = (
    <Card hoverable={!disabled} style={{ height: '100%', opacity: disabled ? 0.55 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 28, color: token.colorLink }}>{icon}</span>
        <Title level={4} style={{ margin: 0 }}>{title}</Title>
        {disabled && <Tag>{comingSoonLabel}</Tag>}
      </div>
      <Paragraph type="secondary" style={{ margin: 0 }}>{desc}</Paragraph>
    </Card>
  );
  if (disabled) return <div style={{ height: '100%', cursor: 'not-allowed' }}>{card}</div>;
  return <a href={href} style={{ display: 'block', height: '100%' }}>{card}</a>;
}

function HomePage() {
  const { t } = useTranslation();
  const comingSoon = t('menu.comingSoon');
  const modules = [
    { href: '/vmt/master/partners', icon: <DatabaseOutlined />,          title: t('menu.masterData'), desc: t('home.desc.master') },
    { href: '/vmt/finance/',        icon: <DollarOutlined />,            title: t('menu.fina'),       desc: t('home.desc.fina') },
    { href: '/vmt/hr/',             icon: <TeamOutlined />,              title: t('menu.hrpy'),       desc: t('home.desc.hrpy') },
    { href: '/vmt/asset/',          icon: <LaptopOutlined />,            title: t('menu.asst'),       desc: t('home.desc.asst') },
    { href: '/vmt/admin',           icon: <SafetyCertificateOutlined />, title: t('menu.admin'),      desc: t('home.desc.admin') },
  ];
  return (
    <div>
      <Title level={3} style={{ marginTop: 0 }}>{t('app.name')} — {t('menu.home')}</Title>
      <Paragraph type="secondary">{t('home.intro')}</Paragraph>
      <Row gutter={[16, 16]}>
        {modules.map((m) => (
          <Col key={m.href} xs={24} sm={12} lg={8} xl={6}>
            <ModuleCard {...m} comingSoonLabel={comingSoon} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

function LoginPage() {
  const { t } = useTranslation();
  const { login, loginWithGoogle, context } = usePermissions();
  const navigate = useNavigate();
  useEffect(() => { if (context) navigate('/'); }, [context, navigate]);
  return (
    <>
      <PreAuthToolbar />
      <LoginForm
        title={t('app.name')}
        subtitle={t('app.company')}
        onLogin={({ username, password, totpCode }) => login({ username, password, totpCode })}
        onGoogleLogin={async (idToken) => loginWithGoogle({ idToken })}
      />
    </>
  );
}

// Admin dashboard fallback: user thiếu Auth.Audit.ViewStats → redirect /admin/profile.
function AdminDashboardGuard({ axios }) {
  const { has } = usePermissions();
  if (!has('Auth.Audit.ViewStats')) return <Navigate to="/admin/profile" replace />;
  return <DashboardPage axios={axios} />;
}

export function AppShell() {
  const navigate = useNavigate();
  const handleLogout = useCallback(() => navigate('/login'), [navigate]);
  const axios = useMemo(() => createAuthAxios({ baseURL: '', onLogout: handleLogout }), [handleLogout]);
  // Shell cũng phải setSharedAxios (như mọi SPA) — util dùng chung của @vmt/shared
  // (VMTGrid grid-columns...) cần axios để gọi API.
  useEffect(() => setSharedAxios(axios), [axios]);

  return (
    <AntApp>
      <AuthProvider axios={axios} onAuthError={handleLogout}>
        <SessionGate>
          <Routes>
            {/* Pre-auth: no layout */}
            <Route path="/login" element={<LoginPage />} />

            {/* Business: AppLayout */}
            <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route path="/"          element={<HomePage />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
            </Route>

            {/* Admin AuthSystem: AdminLayout, mount /admin/* */}
            <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index               element={<AdminDashboardGuard axios={axios} />} />
              <Route path="users"        element={<UsersPage          axios={axios} />} />
              <Route path="roles"        element={<RolesPage          axios={axios} />} />
              <Route path="permissions"  element={<PermissionsPage    axios={axios} />} />
              <Route path="policies"     element={<PoliciesPage       axios={axios} />} />
              <Route path="audit"        element={<AuditPage          axios={axios} />} />
              <Route path="change-log"   element={<ChangeLogPage      axios={axios} />} />
              <Route path="alerts"       element={<AlertsPage         axios={axios} />} />
              <Route path="profile"      element={<ProfilePage        axios={axios} />} />
              <Route path="sessions"     element={<SessionsPage       axios={axios} />} />
              <Route path="branding"     element={<BrandingPage       axios={axios} />} />
              <Route path="smtp"         element={<SmtpConfigPage     axios={axios} />} />
              <Route path="google-oauth" element={<GoogleOAuthPage    axios={axios} />} />
              <Route path="security"     element={<SecurityConfigPage axios={axios} />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SessionGate>
      </AuthProvider>
    </AntApp>
  );
}
